<template>
  <IosGlassModal
    :visible="visible"
    title="上传诊断日志"
    :primary-text="resultReportId ? '关闭' : '上传'"
    :secondary-text="resultReportId ? copyButtonText : '取消'"
    :secondary-highlighted="copyButtonHighlighted"
    :close-on-secondary="!resultReportId"
    :primary-loading="uploading"
    :primary-disabled="!resultReportId && !canUpload"
    @update:visible="$emit('update:visible', $event)"
    @cancel="handleCancel"
    @confirm="handleConfirm"
  >
    <div class="log-upload-form">
      <div v-if="displayUserId" class="log-upload-user-id">
        <span class="log-upload-user-id-label">您的用户 ID</span>
        <span class="log-upload-user-id-value">{{ displayUserId }}</span>
        <span class="log-upload-user-id-hint">联系开发者时可提供此 ID，便于快速定位日志</span>
      </div>
      <div v-else class="log-upload-user-id log-upload-guest-id">
        <span class="log-upload-user-id-label">访客标识（未登录）</span>
        <span class="log-upload-user-id-value log-upload-guest-id-value">{{ guestId }}</span>
        <span class="log-upload-user-id-hint">建议登录后反馈；若不便登录，请优先提供下方的反馈编号</span>
      </div>

      <textarea
        v-model="description"
        class="log-upload-textarea"
        rows="4"
        maxlength="500"
        placeholder="请描述你遇到的问题，例如：播放音乐时卡住、搜索无结果等"
      />

      <div class="log-upload-range">
        <button
          type="button"
          class="range-pill"
          :class="{ active: selectedMinutes === 15 }"
          @click="selectRange(15)"
        >
          最近 15 分钟
        </button>
        <button
          type="button"
          class="range-pill"
          :class="{ active: selectedMinutes === 60 }"
          @click="selectRange(60)"
        >
          最近 1 小时
        </button>
      </div>

      <div class="log-upload-stats">
        <div>约 {{ preview.lineCount || 0 }} 条日志</div>
        <div>原始 {{ preview.rawBytesLabel || '0 B' }} · 压缩后约 {{ preview.compressedBytesLabel || '0 B' }}</div>
        <div v-if="preview.logStartTime && preview.logEndTime" class="log-upload-range-text">
          时间范围：{{ preview.logStartTime }} ~ {{ preview.logEndTime }}
        </div>
      </div>

      <div v-if="resultReportId" class="log-upload-success">
        上传成功<br />
        <template v-if="displayUserId">用户 ID：{{ displayUserId }}<br /></template>
        <template v-else>访客标识：{{ guestId }}<br /></template>
        反馈编号：{{ resultReportId }}
      </div>
      <div v-if="errorMessage" class="log-upload-error">
        {{ errorMessage }}
      </div>
    </div>
  </IosGlassModal>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { useUserStore } from '@/store/user';
import IosGlassModal from '@/components/common/IosGlassModal.vue';
import { previewLogReport, uploadLogReport } from '@/utils/logger/logReportService';
import { getOrCreateGuestId } from '@/utils/logger/guestId';

const props = defineProps({
  visible: { type: Boolean, default: false },
});

const emit = defineEmits(['update:visible', 'uploaded']);

const userStore = useUserStore();
const displayUserId = computed(() => (userStore.isLoggedIn ? (userStore.currentUser?.id ?? null) : null));
// 未登录时使用本地持久化的访客标识，用于在服务器端归档反馈
const guestId = computed(() => (displayUserId.value ? null : getOrCreateGuestId()));

const description = ref('');
const selectedMinutes = ref(15);
const preview = ref({
  lineCount: 0,
  rawBytesLabel: '0 B',
  compressedBytesLabel: '0 B',
  logStartTime: null,
  logEndTime: null,
});
const uploading = ref(false);
const errorMessage = ref('');
const resultReportId = ref('');
const COPY_BUTTON_DEFAULT = '复制反馈编号';
const COPY_BUTTON_SUCCESS = '已复制';
const copyButtonText = ref(COPY_BUTTON_DEFAULT);
const copyButtonHighlighted = ref(false);
let copyResetTimer = null;

const canUpload = computed(() => !uploading.value && (preview.value.lineCount || 0) > 0);

function resetCopyButtonState() {
  if (copyResetTimer) {
    clearTimeout(copyResetTimer);
    copyResetTimer = null;
  }
  copyButtonText.value = COPY_BUTTON_DEFAULT;
  copyButtonHighlighted.value = false;
}

function showCopySuccessOnButton() {
  resetCopyButtonState();
  copyButtonText.value = COPY_BUTTON_SUCCESS;
  copyButtonHighlighted.value = true;
  copyResetTimer = setTimeout(() => {
    copyButtonText.value = COPY_BUTTON_DEFAULT;
    copyButtonHighlighted.value = false;
    copyResetTimer = null;
  }, 3000);
}

async function refreshPreview() {
  try {
    preview.value = await previewLogReport(selectedMinutes.value);
    errorMessage.value = '';
  } catch (error) {
    preview.value = {
      lineCount: 0,
      rawBytesLabel: '0 B',
      compressedBytesLabel: '0 B',
      logStartTime: null,
      logEndTime: null,
    };
  }
}

async function selectRange(minutes) {
  selectedMinutes.value = minutes;
  await refreshPreview();
}

async function handleConfirm() {
  if (resultReportId.value) {
    emit('update:visible', false);
    return;
  }
  await handleUpload();
}

async function handleCancel() {
  if (resultReportId.value) {
    await handleCopyReportId();
    return;
  }
  emit('update:visible', false);
}

async function handleCopyReportId() {
  if (!resultReportId.value) {
    return;
  }
  try {
    await navigator.clipboard.writeText(resultReportId.value);
    showCopySuccessOnButton();
  } catch (error) {
    message.error('复制失败，请手动复制反馈编号');
  }
}

async function handleUpload() {
  if (!canUpload.value) {
    return;
  }
  uploading.value = true;
  errorMessage.value = '';
  resultReportId.value = '';
  try {
    const result = await uploadLogReport({
      description: description.value,
      minutes: selectedMinutes.value,
      guestId: guestId.value,
    });
    resultReportId.value = result?.reportId || '';
    const idText = displayUserId.value
      ? `，用户 ID：${displayUserId.value}`
      : `，访客标识：${guestId.value}`;
    message.success(`日志上传成功，反馈编号：${resultReportId.value}${idText}`);
    emit('uploaded', result);
  } catch (error) {
    errorMessage.value = error?.message || '日志上传失败，请稍后重试';
  } finally {
    uploading.value = false;
  }
}

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) {
      resetCopyButtonState();
      return;
    }
    errorMessage.value = '';
    resultReportId.value = '';
    resetCopyButtonState();
    await refreshPreview();
  },
);

onBeforeUnmount(() => {
  resetCopyButtonState();
});
</script>

<style scoped>
.log-upload-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.log-upload-user-id {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(0, 122, 255, 0.1);
  box-shadow: inset 0 0 0 1px rgba(0, 122, 255, 0.18);
}

.log-upload-user-id-label {
  font-size: 12px;
  color: #3c3c43;
  font-weight: 600;
}

.log-upload-user-id-value {
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: #007aff;
  letter-spacing: 0.02em;
}

.log-upload-user-id-hint {
  font-size: 11px;
  line-height: 1.4;
  color: #636366;
  text-align: center;
}

.log-upload-guest-id-value {
  font-size: 14px;
  word-break: break-all;
}

.log-upload-textarea {
  width: 100%;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 92px;
  color: #111;
}

.log-upload-textarea:focus {
  outline: 2px solid rgba(0, 122, 255, 0.25);
}

.log-upload-range {
  display: flex;
  gap: 8px;
}

.range-pill {
  flex: 1;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: rgba(118, 118, 128, 0.12);
  color: #111;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.range-pill.active {
  background: rgba(0, 122, 255, 0.16);
  box-shadow: inset 0 0 0 1px rgba(0, 122, 255, 0.45);
}

.log-upload-stats {
  font-size: 12px;
  line-height: 1.6;
  color: #3c3c43;
  text-align: center;
  padding: 0 4px;
}

.log-upload-range-text {
  margin-top: 2px;
  word-break: break-all;
}

.log-upload-success {
  text-align: center;
  color: #137333;
  font-size: 13px;
  font-weight: 600;
}

.log-upload-error {
  text-align: center;
  color: #b42318;
  font-size: 13px;
}
</style>
